/* Utils */
import {isApiPaginationLimitExceeded} from '../../common/network/apiUtils';
import {generateFilterApiQueryParams} from '../filter/filterUtils';
import {matchUrlForRoutePath} from '../../core/route/routeUtils';
import {generateQueryString} from '../../common/url/urlUtils';
import {generateStructuredBreadCrumbDataProps} from '../seo/seoUtils';

/* Configs */
import appConfig from '../../appConfig';

/* Constants */
import {
  PLATFORMS,
  ROUTE_PATHS,
} from '../../core/route/routeConstants';
import {TOGGLED_BRAND_PAGINATION_PAGE_NUMBER_LIMIT} from './brandPaginationConstants';
import {HTTP_STATUS_CODES} from '../../common/network/httpConstants';
import {FORBIDDEN_URLS} from './brandConstants';
import {FACET_QUERY_PARAM_NAMES} from '../../views/filter/facet/facetConstants';
import {DELIVERY} from '../delivery/deliveryConstants';

function isBrandPageApiPaginationLimitExceeded(pageNumber, isToggledPageLimitActive = false) {
  return (
    (isToggledPageLimitActive &&
      parseInt(pageNumber) > TOGGLED_BRAND_PAGINATION_PAGE_NUMBER_LIMIT) ||
    isApiPaginationLimitExceeded(pageNumber)
  );
}

function extractBrandId(brandUrl) {
  let rawBrandUrl;
  let brandId;

  if (brandUrl[0] === '/') {
    rawBrandUrl = brandUrl;
  } else {
    rawBrandUrl = '/' + categoryUrl;
  }

  const matchResult = matchUrlForRoutePath(rawBrandUrl, ROUTE_PATHS[PLATFORMS.MOBILE].BRAND_PAGE);

  if (matchResult) {
    ({brandId} = matchResult.params);
  } else {
    brandId = null;
  }

  return {
    brandId,
  };
}

function generateBrandPageUrl(brandId, queryParams) {
  let url = `/${brandId}`;
  if (queryParams) {
    const queryString = generateQueryString(
      {
        ...queryParams,
      },
      {
        withPrefix: true,
        encode: false,
      },
    );

    if (queryString) {
      url += queryString;
    }
  }

  return url;
}

function checkForbiddenUrl(url, forbiddenUrls) {
  const isForbiddenUrl = forbiddenUrls.filter(forbiddenUrl => {
    return url.startsWith(forbiddenUrl);
  });

  return Boolean(isForbiddenUrl?.length);
}

function checkForBrandPageForbiddenUrlsRedirection(pathname) {
  let redirection;
  const checkoutRedirection = checkForbiddenUrl(pathname, FORBIDDEN_URLS.CHECKOUT.FROM) && FORBIDDEN_URLS.CHECKOUT.TO;

  if (checkoutRedirection) {
    redirection = {
      url: checkoutRedirection,
    };
  }

  return redirection;
}

function checkForBrandPageRedirection(
  brandResponseData,
  brandId,
  currentQueryParams,
  appliedFacetArray,
) {
  let redirection;

  if (!brandResponseData) {
    return null;
  }

  const isAppliedCategoryFilter = Boolean(currentQueryParams?.[FACET_QUERY_PARAM_NAMES.CATEGORY]);
  const isAppliedJetDeliveryFilter = Boolean(appliedFacetArray?.filter(filter => filter.id === DELIVERY.NEXT_DAY_DELIVERY)?.length);
  const isAppliedFilterExcludingJetDelivery = Boolean(appliedFacetArray?.filter(filter => filter.id !== DELIVERY.NEXT_DAY_DELIVERY)?.length);
  const isAppliedFilter = Boolean(isAppliedFilterExcludingJetDelivery || isAppliedCategoryFilter);

  if (brandResponseData?.redirection && brandResponseData?.redirection?.url) {
    redirection = {
      ...brandResponseData.redirection,
    };
  } else if (brandResponseData?.data?.redirectUrl) {
    redirection = {
      url: brandResponseData?.data?.redirectUrl,
    };
  } else if (brandResponseData?.statusCode === HTTP_STATUS_CODES.OK && brandResponseData?.data?.products?.length === 0) {
    if (isAppliedFilter && !isAppliedJetDeliveryFilter) {
      redirection = {
        isInternal: true,
        type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
        url: generateBrandPageUrl(
          brandId,
        ),
      };
    } else if (!isAppliedFilter && !isAppliedJetDeliveryFilter) {
      redirection = {
        isInternal: true,
        type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
        url: '/markalar',
      };
    }
  }

  return redirection;
}

/**
 *
 * @param moriaRedirection
 * @param seoState
 * @param brandId
 * @param currentQueryParams
 * @returns {{isInternal: boolean, type: number, url: string}}
 */
function checkForDesktopBrandPageRedirection(
  moriaRedirection,
  seoState,
  brandId,
  currentQueryParams,
) {
  let redirection;


  const isAppliedCategoryFilter = Boolean(currentQueryParams?.[FACET_QUERY_PARAM_NAMES.CATEGORY]);
  const isAppliedJetDeliveryFilter = Boolean(seoState?.appliedFilters?.filter(filter => filter.id === DELIVERY.NEXT_DAY_DELIVERY)?.length);
  const isAppliedFilterExcludingJetDelivery = Boolean(seoState?.appliedFilters?.filter(filter => filter.id !== DELIVERY.NEXT_DAY_DELIVERY)?.length);
  const isAppliedFilter = Boolean(isAppliedFilterExcludingJetDelivery || isAppliedCategoryFilter);

  if (moriaRedirection && moriaRedirection?.url) {
    redirection = {
      ...moriaRedirection,
    };
  } else if (seoState?.totalProductCount === 0){
    if (isAppliedFilter && !isAppliedJetDeliveryFilter) {
      redirection = {
        isInternal: true,
        type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
        url: generateBrandPageUrl(
          brandId,
        ),
      };
    } else if (!isAppliedFilter && !isAppliedJetDeliveryFilter) {
      redirection = {
        isInternal: true,
        type: HTTP_STATUS_CODES.MOVED_PERMANENTLY,
        url: '/markalar',
      };
    }
  }

  return redirection;
}

function generateBrandPageFilterQueryParams(selectedFiltersByFacetMap) {
  return generateFilterApiQueryParams(
    selectedFiltersByFacetMap,
    {
      ignoredFacets: [
        null,
      ],
    },
  );
}

function generateBrandBreadCrumbConfigs(brandBreadCrumbArray) {
  return [{
    id: '/',
    labelText: 'Anasayfa',
    url: '/',
    isRoot: true,
    ...generateStructuredBreadCrumbDataProps(),
  }].concat(brandBreadCrumbArray.map(
    breadCrumb => {
      let url = breadCrumb.name === 'Markalar' ? appConfig.baseUrl + '/' + breadCrumb.url : breadCrumb.url.startsWith('/') ? breadCrumb.url : '/' + breadCrumb.url
      return {
        id: breadCrumb.url,
        labelText: breadCrumb.name,
        url: url,
        ...generateStructuredBreadCrumbDataProps(),
      };
    }
  ));
}

export {
  checkForBrandPageForbiddenUrlsRedirection,
  checkForBrandPageRedirection,
  generateBrandPageFilterQueryParams,
  isBrandPageApiPaginationLimitExceeded,
  extractBrandId,
  generateBrandBreadCrumbConfigs,
  checkForDesktopBrandPageRedirection
}
